import React from 'react';
import { Star, Store, DollarSign, Clock, Smile, ArrowRight } from 'lucide-react';

// Customer reviews (replacing former restaurant testimonials per redesigned homepage)
const customerReviews = [
  {
    name: 'Amanda R.',
    quote: '“Food actually costs less here.”',
    body: 'Finally found a platform that doesn’t overcharge me. Same great food, lower prices.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=128&h=128&fit=crop&q=80',
    stars: 5,
  },
  {
    name: 'Daniel T.',
    quote: '“No hidden fees anymore.”',
    body: 'What you see is what you pay. No surprise fees at checkout. I love it!',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=128&h=128&fit=crop&q=80',
    stars: 5,
  },
  {
    name: 'Jessica M.',
    quote: '“Much better than other apps.”',
    body: 'Great local restaurants, better prices, and the service is amazing. Highly recommend!',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=128&h=128&fit=crop&q=80',
    stars: 5,
  },
];

const stats = [
  { icon: Store,      value: '2,500+', label1: 'Restaurants', label2: 'Trust NoFee' },
  { icon: DollarSign, value: '$2.7M+', label1: 'Saved',       label2: 'In Commission Fees' },
  { icon: Clock,      value: 'Direct', label1: 'Stripe',      label2: 'Payouts' },
  { icon: Smile,      value: '98%',    label1: 'Customer',    label2: 'Satisfaction' },
];

const TrustedReviewsSection = () => {
  return (
    <>
      {/* CUSTOMER REVIEWS */}
      <section
        id="testimonials"
        className="relative bg-black pt-14 pb-10 lg:pt-20 lg:pb-14"
        data-testid="customer-reviews-section"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
              Why customers are switching to <span className="text-gold-400">NoFee</span>
            </h2>
            <a
              href="#reviews"
              className="text-sm font-bold text-gold-400 hover:text-gold-300 inline-flex items-center gap-1"
              data-testid="view-all-reviews-link"
            >
              View all reviews <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {customerReviews.map((r) => (
              <div
                key={r.name}
                data-testid={`review-card-${r.name.replace(/\./g, '').trim().replace(/\s+/g, '-').toLowerCase()}`}
                className="rounded-2xl bg-gradient-to-br from-zinc-950 to-black border border-zinc-800 hover:border-gold-500/30 p-6 transition-colors"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${i < r.stars ? 'fill-gold-400 text-gold-400' : 'text-zinc-700'}`}
                    />
                  ))}
                </div>
                <p className="text-[15px] font-extrabold text-white leading-tight">{r.quote}</p>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{r.body}</p>
                <div className="mt-5 flex items-center gap-3">
                  <img
                    src={r.avatar}
                    alt={r.name}
                    loading="lazy"
                    className="w-9 h-9 rounded-full object-cover ring-1 ring-gold-500/30"
                  />
                  <span className="text-sm font-bold text-white/85">— {r.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section
        className="relative bg-black pb-14 lg:pb-20"
        data-testid="trust-stats-section"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-6">
            Trusted by Local Restaurants
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {stats.map((s) => (
              <div
                key={s.value}
                data-testid={`trust-stat-${s.value.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                className="rounded-2xl bg-gradient-to-br from-zinc-950 to-black border border-gold-500/15 hover:border-gold-500/35 p-6 lg:p-7 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl border border-gold-500/35 bg-gold-500/[0.05] flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-6 h-6 text-gold-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-3xl lg:text-4xl font-black text-white leading-none tracking-tight">{s.value}</div>
                    <div className="mt-1.5 text-[12px] text-white/55 leading-tight">
                      <div className="font-semibold text-white/80">{s.label1}</div>
                      <div>{s.label2}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default TrustedReviewsSection;
